import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BookingSection } from "@/components/BookingForm";
import { WOMENS_CIRCLE } from "@/data/womensCircle";
import { useLang } from "@/i18n/LanguageContext";
import { useSeo } from "@/hooks/useSeo";

const CIRCLE_DATE_VALUES = ["2026-09-08", "2026-09-22", "2026-10-06", "2026-11-03", "2026-11-17", "2026-12-01", "2026-12-15"];

export default function WomensCirclePage() {
  const { lang } = useLang();
  const wc = WOMENS_CIRCLE[lang] || WOMENS_CIRCLE.en;
  useSeo({ ...wc.seo, image: `/images/circle.jpg` });
  const bookingDates = CIRCLE_DATE_VALUES.map((value, i) => ({ value, label: `${wc.autumn.dates[i]} 2026` }));

  return (
    <main data-testid="womens-circle-page">
      <section className="bg-cream pt-40 pb-20 lg:pt-48 lg:pb-28 grain" data-testid="wc-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.8 }}
              className="mb-6 text-xs tracking-[0.3em] uppercase text-clay"
              data-testid="wc-hero-eyebrow"
            >
              {wc.hero.eyebrow}
            </motion.p>
            <div className="overflow-hidden">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
                className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.0] tracking-tight text-ink"
                data-testid="wc-hero-title"
              >
                {wc.hero.title}
              </motion.h1>
            </div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.6 }}
              className="mt-7 max-w-xl font-serif italic text-2xl leading-snug text-clay"
              data-testid="wc-hero-sub"
            >
              {wc.hero.sub}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.75 }}
              className="mt-7 max-w-xl"
              data-testid="wc-hero-intro"
            >
              <p className="text-base leading-relaxed text-soft">{wc.hero.p1}</p>
              <p className="mt-4 text-base leading-relaxed text-soft">{wc.hero.p2}</p>
            </motion.div>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="overflow-hidden rounded-t-[10rem]">
              <img src="/images/circle.jpg" alt="Women sitting together in a warm circle" className="aspect-[4/5] w-full object-cover" data-testid="wc-hero-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-20 lg:py-28" data-testid="wc-share">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <p className="font-serif text-2xl sm:text-3xl leading-snug text-ink">{wc.hero.p3}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{wc.hero.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="wc-happens">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{wc.happens.title}</h2>
            <p className="mt-7 text-base leading-relaxed text-soft drop-cap">{wc.happens.p1}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{wc.happens.p2}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{wc.happens.p3}</p>
            <p className="mt-5 text-base leading-relaxed text-soft">{wc.happens.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-parchment py-24 lg:py-32" data-testid="wc-together">
        <div className="mx-auto max-w-3xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink">{wc.together.title}</h2>
            <p className="mt-7 font-serif text-2xl leading-snug text-clay italic">{wc.together.p1}</p>
            <p className="mt-6 text-base leading-relaxed text-soft">{wc.together.p2}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{wc.together.p3}</p>
            <p className="mt-4 text-base leading-relaxed text-soft">{wc.together.p4}</p>
          </Reveal>
        </div>
      </section>

      <section className="bg-cream py-24 lg:py-32" data-testid="wc-autumn">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl tracking-tight text-ink mb-10">{wc.autumn.title}</h2>
            <div className="grid gap-px bg-ink/10 border border-ink/10 sm:grid-cols-2" data-testid="wc-dates-list">
              {wc.autumn.dates.map((d) => (
                <p key={d} className="bg-cream px-6 py-4 font-serif text-lg text-ink">{d}</p>
              ))}
              <p className="bg-cream px-6 py-4 text-sm text-soft flex items-center">{wc.autumn.rows.join(" · ")}</p>
            </div>
            <p className="mt-6 text-sm text-soft italic">{wc.autumn.note}</p>
            <p className="mt-2 text-sm text-soft">{wc.autumn.note2}</p>
          </Reveal>
        </div>
      </section>

      <BookingSection offer="womens-circle" dates={bookingDates} tickets={null} priceLabel="€10" />
    </main>
  );
}
